import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    CircularProgress,
    Paper,
    alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TimelineIcon from '@mui/icons-material/Timeline';
import AddCommentIcon from '@mui/icons-material/AddComment';
import {
    Fp1Device,
    Fp1CustodyEvent,
    Fp1DeviceStatus,
    Fp1CustodyEventMetadata,
    formatFp1SubscriptionStatusLabel,
    fp1SubscriptionStatusColor,
} from '../fp1.types';
import {
    getFp1DeviceCustodyHistory,
    getFp1Device,
    updateFp1Device,
    addFp1DeviceNote,
} from '../fp1.api';
import Fp1ModifyAssignmentDialog from './Fp1ModifyAssignmentDialog';
import Fp1HierarchyPodAdminPanel from './Fp1HierarchyPodAdminPanel';
import Fp1SubscriptionOverrideDialog, { OverrideType } from './Fp1SubscriptionOverrideDialog';

interface Props {
    open: boolean;
    device: Fp1Device | null;
    onClose: () => void;
    onSave: () => void;
}

const STATUS_COLORS: Record<Fp1DeviceStatus, 'success' | 'info' | 'warning' | 'error' | 'default'> =
    {
        in_stock: 'success',
        deployed: 'info',
        maintenance: 'warning',
        defective: 'error',
        unknown: 'default',
        returned: 'default',
        decommissioned: 'default',
    };

const TYPE_LABELS: Record<string, string> = {
    fp1: 'FP1',
    fp1_gateway: 'Gateway (FP1)',
};

// "Unknown" clears org/athlete/subscription on the backend (like Defective). "Returned" is
// retained here only so existing returned pods can be re-statused — it is hidden from the
// Devices subtab summary cards and filter.
const STATUS_OPTIONS: Fp1DeviceStatus[] = [
    'in_stock',
    'deployed',
    'maintenance',
    'defective',
    'unknown',
    'returned',
    'decommissioned',
];

const formatStatus = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Replace raw installation UUID in stored notes with the resolved installation name when available. */
const custodyNotesDisplay = (event: Fp1CustodyEvent): string => {
    const raw = event.notes?.trim() || '';
    if (!raw) {
        return '';
    }
    if (event.installation_uuid && event.installation_name) {
        return raw.split(event.installation_uuid).join(event.installation_name);
    }
    return raw;
};

function isFp1BenchCsvImportMetadata(
    meta: Fp1CustodyEventMetadata | undefined,
): meta is Record<string, unknown> {
    return (
        meta != null &&
        typeof meta === 'object' &&
        !Array.isArray(meta) &&
        (meta as Record<string, unknown>)['kind'] === 'fp1_bench_csv_import'
    );
}

const BENCH_META_LABELS: [string, string][] = [
    ['tested_at', 'Date/Time tested'],
    ['current_draw', 'Current without FW'],
    ['fw_version', 'Firmware flashed (column 4)'],
    ['current_2', 'Standby current'],
    ['current_3', 'Live current'],
    ['current_4', 'Charge current'],
    ['vbat', 'VBAT'],
    ['pmid', 'PMID'],
    ['iin', 'IIN'],
    ['ichg', 'ICHG'],
    ['error_message', 'Error (if any)'],
];

const BenchImportMetadataBlock: React.FC<{ metadata: Record<string, unknown> }> = ({
    metadata,
}) => {
    const rows = BENCH_META_LABELS.map(([key, label]) => {
        const raw = metadata[key];
        const value = raw != null && String(raw).trim() !== '' ? String(raw) : '';
        return value ? { label, value } : null;
    }).filter(Boolean) as { label: string; value: string }[];

    const autoDefective = metadata['bench_auto_defective'] === true;

    if (rows.length === 0 && !autoDefective) {
        return null;
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                mt: 1,
                p: 1.5,
                borderRadius: 1,
                bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.06),
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                Bench test data (spreadsheet import)
            </Typography>
            {autoDefective && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
                    Device was marked <strong>Defective</strong> automatically because the Error
                    column was non-empty at receipt.
                </Typography>
            )}
            {rows.length > 0 && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(120px, 38%) 1fr',
                        gap: 0.75,
                        mt: 1,
                    }}
                >
                    {rows.map((r) => (
                        <React.Fragment key={r.label}>
                            <Typography variant="caption" color="text.secondary">
                                {r.label}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                            >
                                {r.value}
                            </Typography>
                        </React.Fragment>
                    ))}
                </Box>
            )}
        </Paper>
    );
};

const formatDate = (d: string | null) =>
    d
        ? new Date(d).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
          })
        : '--';

const Fp1DeviceDetailDialog: React.FC<Props> = ({ open, device, onClose, onSave }) => {
    const [liveDevice, setLiveDevice] = useState<Fp1Device | null>(null);
    const [custodyEvents, setCustodyEvents] = useState<Fp1CustodyEvent[]>([]);
    const [loading, setLoading] = useState(false);
    const [editStatus, setEditStatus] = useState<Fp1DeviceStatus | ''>('');
    const [eventNotes, setEventNotes] = useState('');
    const [noteText, setNoteText] = useState('');
    const [saving, setSaving] = useState(false);
    const [showAddNote, setShowAddNote] = useState(false);
    const [modifyAssignmentOpen, setModifyAssignmentOpen] = useState(false);
    const [subscriptionOverrideOpen, setSubscriptionOverrideOpen] = useState(false);
    // Which tab the subscription dialog opens on: the "Remove subscription status" button
    // jumps straight to the removal tab instead of making the admin find it.
    const [subscriptionOverrideTab, setSubscriptionOverrideTab] = useState<OverrideType>('comp');
    const [editFirmware, setEditFirmware] = useState('');

    useEffect(() => {
        if (open && device) {
            setLiveDevice(device);
            setEditStatus(device.status);
            setEventNotes('');
            setNoteText('');
            setShowAddNote(false);
            setEditFirmware(device.firmware_version?.trim() || '');
            fetchCustodyHistory(device.uuid);
        }
    }, [open, device]);

    const fetchCustodyHistory = async (uuid: string) => {
        setLoading(true);
        try {
            const response = await getFp1DeviceCustodyHistory(uuid);
            // Resource collection may return { data: [...] } or a plain array
            const events = Array.isArray(response.data) ? response.data : response.data?.data ?? [];
            setCustodyEvents(events);
        } catch (err) {
            console.error('Failed to fetch custody history', err);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async () => {
        const active = liveDevice ?? device;
        if (!active || !editStatus || editStatus === active.status) return;
        setSaving(true);
        try {
            await updateFp1Device(active.uuid, {
                status: editStatus,
                event_notes: eventNotes || undefined,
            });
            onSave();
        } catch (err: any) {
            console.error('Failed to update device', err);
        } finally {
            setSaving(false);
        }
    };

    const handleAddNote = async () => {
        const active = liveDevice ?? device;
        if (!active || !noteText.trim()) return;
        setSaving(true);
        try {
            await addFp1DeviceNote(active.uuid, noteText);
            setNoteText('');
            setShowAddNote(false);
            fetchCustodyHistory(active.uuid);
        } catch (err) {
            console.error('Failed to add note', err);
        } finally {
            setSaving(false);
        }
    };

    const handleFirmwareSave = async () => {
        const active = liveDevice ?? device;
        if (!active) return;
        const next = editFirmware.trim();
        const prev = active.firmware_version?.trim() || '';
        if (next === prev) return;
        setSaving(true);
        try {
            await updateFp1Device(active.uuid, {
                firmware_version: next === '' ? null : next,
            });
            onSave();
        } catch (err) {
            console.error('Failed to update firmware', err);
        } finally {
            setSaving(false);
        }
    };

    if (!device) return null;

    const d = liveDevice ?? device;

    const refreshHierarchyFields = async () => {
        const active = liveDevice ?? device;
        try {
            const res = await getFp1Device(active.uuid);
            setLiveDevice(res.data.data);
            onSave();
        } catch (e) {
            console.error('Failed to refresh device', e);
            onSave();
        }
        // Re-fetch custody history so detach/assign events appear immediately
        void fetchCustodyHistory(active.uuid);
    };

    const formatCost = (cents: number | null) =>
        cents != null ? `$${(cents / 100).toFixed(2)}` : '--';

    const subscriptionLabel = formatFp1SubscriptionStatusLabel(
        d.subscription_status,
        d.status === 'deployed',
    );
    const subscriptionChipColor = fp1SubscriptionStatusColor(d.subscription_status);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {d.serial_number}
                    </Typography>
                    <Chip
                        label={TYPE_LABELS[d.device_type] || d.device_type}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                    />
                    <Chip
                        label={`Custody: ${formatStatus(d.status)}`}
                        size="small"
                        color={STATUS_COLORS[d.status]}
                        sx={{ fontWeight: 500 }}
                    />
                    <Chip
                        label={`Sub.: ${subscriptionLabel}`}
                        size="small"
                        variant={subscriptionLabel === 'N/A' ? 'outlined' : 'filled'}
                        color={subscriptionChipColor}
                        sx={{ fontWeight: 500 }}
                    />
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {/* Device Info Grid */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <InfoItem
                        label="Hardware (PCB)"
                        value={d.hardware_version?.trim() || d.model?.trim() || '--'}
                    />
                    <InfoItem label="Firmware" value={d.firmware_version?.trim() || '--'} />
                    <InfoItem label="Unit Cost" value={formatCost(d.unit_cost_cents)} />
                    <InfoItem label="Received" value={formatDate(d.received_at)} />
                    <InfoItem
                        label="Updated"
                        value={formatDate(d.last_activity_at ?? d.updated_at)}
                    />
                    <InfoItem label="Decommissioned" value={formatDate(d.decommissioned_at)} />
                    <InfoItem label="Notes" value={d.notes || '--'} />
                    <Box sx={{ gridColumn: '1 / -1', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setModifyAssignmentOpen(true)}
                        >
                            Modify assignment
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                setSubscriptionOverrideTab('comp');
                                setSubscriptionOverrideOpen(true);
                            }}
                        >
                            Set subscription status
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={() => {
                                setSubscriptionOverrideTab('none');
                                setSubscriptionOverrideOpen(true);
                            }}
                        >
                            Remove subscription status
                        </Button>
                        {d.subscription_cleared_at ? (
                            <Chip
                                size="small"
                                variant="outlined"
                                label="Subscription status removed"
                                sx={{ alignSelf: 'center' }}
                            />
                        ) : null}
                    </Box>
                </Box>

                <Fp1HierarchyPodAdminPanel device={d} onChanged={refreshHierarchyFields} />

                <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                        Update firmware
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}
                    >
                        Hardware (PCB) is set at receipt and cannot be changed here.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <TextField
                            size="small"
                            label="Firmware version on pod"
                            value={editFirmware}
                            onChange={(e) => setEditFirmware(e.target.value)}
                            sx={{ flex: 1 }}
                            placeholder="e.g. 2.4.1"
                        />
                        <Button
                            variant="contained"
                            onClick={handleFirmwareSave}
                            disabled={
                                saving || editFirmware.trim() === (d.firmware_version?.trim() || '')
                            }
                            size="medium"
                        >
                            {saving ? <CircularProgress size={20} /> : 'Save firmware'}
                        </Button>
                    </Box>
                </Paper>

                {/* Status Update */}
                <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                        Update custody status
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}
                    >
                        Subscription status is managed automatically (comp orders and Stripe).
                        Moving a device to a warehouse custody status clears subscription status to
                        N/A.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Custody status</InputLabel>
                            <Select
                                value={editStatus}
                                onChange={(e) => setEditStatus(e.target.value as Fp1DeviceStatus)}
                                label="Custody status"
                            >
                                {STATUS_OPTIONS.map((s) => (
                                    <MenuItem key={s} value={s}>
                                        {formatStatus(s)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label="Event Notes"
                            value={eventNotes}
                            onChange={(e) => setEventNotes(e.target.value)}
                            sx={{ flex: 1 }}
                            placeholder="Reason for status change..."
                        />
                        <Button
                            variant="contained"
                            onClick={handleStatusUpdate}
                            disabled={saving || editStatus === d.status || !editStatus}
                            size="medium"
                        >
                            {saving ? <CircularProgress size={20} /> : 'Update'}
                        </Button>
                    </Box>
                </Paper>

                {/* Custody Timeline */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimelineIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Chain of Custody
                        </Typography>
                    </Box>
                    <Button
                        size="small"
                        startIcon={<AddCommentIcon />}
                        onClick={() => setShowAddNote(!showAddNote)}
                        variant="outlined"
                    >
                        Add Note
                    </Button>
                </Box>

                {showAddNote && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Add a note to this device's history..."
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                        />
                        <Button
                            variant="contained"
                            onClick={handleAddNote}
                            disabled={saving || !noteText.trim()}
                            size="small"
                        >
                            Save
                        </Button>
                    </Box>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <Box sx={{ position: 'relative', pl: 3 }}>
                        {/* Timeline line */}
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 10,
                                top: 0,
                                bottom: 0,
                                width: 2,
                                bgcolor: 'divider',
                            }}
                        />
                        {custodyEvents.map((event, idx) => {
                            const custodyNoteDisplay = custodyNotesDisplay(event);
                            const benchMeta = isFp1BenchCsvImportMetadata(event.metadata)
                                ? event.metadata
                                : null;
                            return (
                                <Box key={event.id} sx={{ position: 'relative', mb: 2 }}>
                                    {/* Timeline dot */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: -21,
                                            top: 6,
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            bgcolor: idx === 0 ? 'primary.main' : 'grey.400',
                                            border: '2px solid',
                                            borderColor: 'background.paper',
                                        }}
                                    />
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 1.5,
                                            bgcolor:
                                                idx === 0
                                                    ? (theme) =>
                                                          alpha(theme.palette.primary.main, 0.04)
                                                    : 'transparent',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                mb: 0.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <Chip
                                                    label={formatStatus(event.event_type)}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.7rem', height: 22 }}
                                                />
                                                {event.from_status &&
                                                    event.to_status &&
                                                    event.from_status !== event.to_status && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {formatStatus(event.from_status)} &rarr;{' '}
                                                            {formatStatus(event.to_status)}
                                                        </Typography>
                                                    )}
                                            </Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDate(event.created_at)}
                                            </Typography>
                                        </Box>
                                        {(event.performed_by_name || event.performed_by_uuid) && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block', mt: 0.25 }}
                                            >
                                                {event.performed_by_name
                                                    ? `Performed by ${event.performed_by_name}`
                                                    : 'Performed by (name unavailable)'}
                                            </Typography>
                                        )}
                                        {custodyNoteDisplay && (
                                            <Typography
                                                variant="body2"
                                                sx={{ mt: 0.5, color: 'text.primary' }}
                                            >
                                                {custodyNoteDisplay}
                                            </Typography>
                                        )}
                                        {benchMeta && (
                                            <BenchImportMetadataBlock metadata={benchMeta} />
                                        )}
                                    </Paper>
                                </Box>
                            );
                        })}
                        {custodyEvents.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                No custody events recorded.
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>

            {modifyAssignmentOpen && (
                <Fp1ModifyAssignmentDialog
                    open={modifyAssignmentOpen}
                    device={d}
                    onClose={() => setModifyAssignmentOpen(false)}
                    onSaved={() => {
                        void refreshHierarchyFields();
                    }}
                />
            )}

            {subscriptionOverrideOpen && (
                <Fp1SubscriptionOverrideDialog
                    open={subscriptionOverrideOpen}
                    initialType={subscriptionOverrideTab}
                    device={d}
                    onClose={() => setSubscriptionOverrideOpen(false)}
                    onSaved={() => {
                        void refreshHierarchyFields();
                    }}
                />
            )}
        </Dialog>
    );
};

const InfoItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value}
        </Typography>
    </Box>
);

export default Fp1DeviceDetailDialog;
